import type { DecorationCss } from "./types";

export const DECOR_FONT_VAR = "fontPoint";

const POINT_FONT = `--${DECOR_FONT_VAR}`;
const FIT_PREFIX = "--fontFit";
const FIT_VAR = `${FIT_PREFIX}-x`;
const FIT = `var(${FIT_VAR}, 1)`;
const NUM_FIT_PREFIX = `${FIT_PREFIX}-num`;
const NUM_FIT = `var(${NUM_FIT_PREFIX}-x, ${FIT})`;
const FIT_AXES = ["x", "cap", "line", "width"] as const;
const NAMED_GUARD_PREFIX = "--fontNamed";
const BUNDLED_FAMILY = '"Bounded"';
const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;
const NO_FAMILIES: readonly string[] = Object.freeze([]);

const IMPORTANT = /\s*!important\s*$/i;
const CSS_WIDE = /^(inherit|initial|unset|revert|revert-layer)$/i;
const SIZE_KEYWORD = /^(xx-small|x-small|small|medium|large|x-large|xx-large|xxx-large|smaller|larger|math)$/i;
const DIMENSION = /^-?(\d+\.?\d*|\.\d+)[a-z%]+$/i;
const FONT_RELATIVE = /^-?(\d+\.?\d*|\.\d+)(em|ex|ch|cap|ic|%)$/i;
const SIZE_FUNCTION = /^(calc|min|max|clamp|var)\(/i;
const UNITLESS = /^(\d+\.?\d*|\.\d+)$/;
const SHORTHAND_PREFIX =
	/^(normal|italic|oblique|small-caps|bold|bolder|lighter|(ultra-|extra-|semi-)?(condensed|expanded)|\d+\.?\d*|-?[\d.]+(deg|rad|grad|turn))$/i;
const GENERIC_FAMILY = /^(serif|sans-serif|monospace|cursive|fantasy|system-ui|math|emoji|fangsong|ui-(serif|sans-serif|monospace|rounded))$/i;
const FAMILY_VAR = /^--.+-font-family$/;
const VAR_REFERENCE = /^var\(\s*(--[\w-]+)\s*(,[\s\S]*)?\)$/;
const NUM_REFERENCE = /var\(\s*--font-num\s*[,)]/;

type NamedFamilies = ReadonlyMap<string, string>;

interface FitContext {
	named: NamedFamilies;
	fit: string;
}

const encoder = new TextEncoder();

const namedCache = new WeakMap<readonly string[], NamedFamilies>();

export const fontFamilyKey = (family: string) => {
	let hash = FNV_OFFSET;
	for (const byte of encoder.encode(family.trim().toLowerCase())) hash = Math.imul(hash ^ byte, FNV_PRIME);
	return (hash >>> 0).toString(16).padStart(8, "0");
};

const splitTopLevel = (value: string, separator: RegExp): string[] => {
	const parts: string[] = [];
	let part = "";
	let depth = 0;
	let quote = "";
	const flush = () => {
		if (part.trim()) parts.push(part.trim());
		part = "";
	};
	for (const char of value) {
		if (quote) {
			if (char === quote) quote = "";
		} else if (char === '"' || char === "'") quote = char;
		else if (char === "(") depth++;
		else if (char === ")") depth--;
		else if (depth === 0 && separator.test(char)) {
			flush();
			continue;
		}
		part += char;
	}
	flush();
	return parts;
};

const coreOf = (value: string) => value.replace(IMPORTANT, "").trim();

const FAMILY_PROPS = new Set(["font-family", "--font-text", "--font-num"]);

const isFamilyProp = (prop: string) => FAMILY_PROPS.has(prop) || FAMILY_VAR.test(prop);

const familyNameKey = (family: string) => family.replace(/["']/g, "").replace(/\s+/g, " ").trim().toLowerCase();

const namedFamiliesOf = (families: readonly string[]): NamedFamilies => {
	let named = namedCache.get(families);
	if (!named) {
		named = new Map(families.map((family) => [familyNameKey(family), family]));
		namedCache.set(families, named);
	}
	return named;
};

const namedFamilyOf = (value: unknown, named: NamedFamilies): string | undefined => {
	if (typeof value !== "string" || named.size === 0) return undefined;
	for (const token of splitTopLevel(coreOf(value), /,/)) {
		const family = GENERIC_FAMILY.test(token) || CSS_WIDE.test(token) ? undefined : named.get(familyNameKey(token));
		if (family) return family;
	}
	return undefined;
};

const customFamiliesOf = (families: string[]) => {
	let count = families.length;
	while (count > 0 && GENERIC_FAMILY.test(families[count - 1])) count--;
	return families.slice(0, count);
};

const familyReferenceOf = (custom: string[]) => {
	const reference = custom.length === 1 ? VAR_REFERENCE.exec(custom[0])?.[1] : undefined;
	return reference && isFamilyProp(reference) ? reference : undefined;
};

const guardOf = (prop: string) => `${NAMED_GUARD_PREFIX}${prop.slice(1)}`;

const guardValueOf = (prop: string, value: string, named: NamedFamilies) => {
	const core = coreOf(value);
	if (CSS_WIDE.test(core)) return core;
	if (namedFamilyOf(core, named)) return `var(${prop})`;
	const reference = familyReferenceOf(customFamiliesOf(splitTopLevel(core, /,/)));
	return reference ? `var(${guardOf(reference)})` : "initial";
};

const isSize = (value: string) => DIMENSION.test(value) || SIZE_FUNCTION.test(value);

const fitSize = (value: string, { fit }: FitContext) =>
	!isSize(value) || FONT_RELATIVE.test(value) || value.includes(FIT_VAR) ? value : `calc(${value} * ${fit})`;

const fitLineHeight = (value: string, { fit }: FitContext) => (UNITLESS.test(value) ? `calc(${value} / ${fit})` : value);

const pointStackOf = (custom: string[], { named }: FitContext) => {
	const point = `var(${POINT_FONT}, ${custom.join(", ")})`;
	const reference = named.size > 0 ? familyReferenceOf(custom) : undefined;
	return reference ? `var(${guardOf(reference)}, ${point})` : point;
};

const fitFamily = (value: string, context: FitContext) => {
	if (CSS_WIDE.test(value) || value.includes(POINT_FONT) || namedFamilyOf(value, context.named)) return value;
	const families = splitTopLevel(value, /,/);
	const custom = customFamiliesOf(families);
	if (!custom.length) return `var(${POINT_FONT}, ${value})`;
	const bundled = custom.some((family) => /bounded/i.test(family)) ? [] : [BUNDLED_FAMILY];
	return [pointStackOf(custom, context), ...bundled, ...families.slice(custom.length)].join(", ");
};

const lineHeightAfter = (tokens: string[], at: number) => {
	const token = tokens[at];
	if (!token?.startsWith("/")) return { lineHeight: undefined, familyAt: at };
	return token === "/" ? { lineHeight: tokens[at + 1], familyAt: at + 2 } : { lineHeight: token.slice(1), familyAt: at + 1 };
};

const shorthandOf = (value: string) => {
	const tokens = splitTopLevel(value, /\s/);
	const sizeAt = tokens.findIndex((token) => !SHORTHAND_PREFIX.test(token));
	if (sizeAt < 0) return null;
	const [size, inlineLineHeight] = splitTopLevel(tokens[sizeAt], /\//);
	if (!size || !(isSize(size) || SIZE_KEYWORD.test(size))) return null;
	const { lineHeight, familyAt } =
		inlineLineHeight === undefined ? lineHeightAfter(tokens, sizeAt + 1) : { lineHeight: inlineLineHeight, familyAt: sizeAt + 1 };
	const family = tokens.slice(familyAt).join(" ");
	return family ? { prefix: tokens.slice(0, sizeAt), size, lineHeight, family } : null;
};

const shorthandFamilyOf = (value: unknown) => (typeof value === "string" ? shorthandOf(coreOf(value))?.family : undefined);

const fitShorthand = (value: string, context: FitContext) => {
	const shorthand = shorthandOf(value);
	if (!shorthand) return value;
	const { prefix, size, lineHeight, family } = shorthand;
	const own: FitContext = { ...context, fit: context.named.size > 0 && NUM_REFERENCE.test(family) ? NUM_FIT : FIT };
	const fittedSize = lineHeight === undefined ? fitSize(size, own) : `${fitSize(size, own)}/${fitLineHeight(lineHeight, own)}`;
	return [...prefix, fittedSize, fitFamily(family, own)].join(" ");
};

const fitFontVar = (value: string, context: FitContext) => (splitTopLevel(value, /\s/).length === 1 ? fitSize(value, context) : fitShorthand(value, context));

type Fitter = (value: string, context: FitContext) => string;

const fitterOf = (prop: string): Fitter | null => {
	if (isFamilyProp(prop)) return fitFamily;
	if (prop === "font-size" || /^--.+-font-size$/.test(prop)) return fitSize;
	if (prop === "line-height") return fitLineHeight;
	if (prop === "font") return fitShorthand;
	if (/^--.+-font$/.test(prop)) return fitFontVar;
	return null;
};

const withPriority = (value: string, fit: Fitter, context: FitContext) => {
	const core = coreOf(value);
	const fitted = fit(core, context);
	if (fitted === core) return value;
	return IMPORTANT.test(value) ? `${fitted} !important` : fitted;
};

const fitOf = (css: DecorationCss, named: NamedFamilies) =>
	named.size > 0 && [css["font-family"], css.font].some((value) => typeof value === "string" && NUM_REFERENCE.test(value)) ? NUM_FIT : FIT;

const axisVars = (prefix: string, value: (axis: string) => string) => FIT_AXES.map((axis) => [`${prefix}-${axis}`, value(axis)] as const);

const familyFitVars = (prefix: string, family: string) => {
	const key = fontFamilyKey(family);
	return axisVars(prefix, (axis) => `var(${FIT_PREFIX}-f-${key}-${axis}, 1)`);
};

const namedVarsOf = (css: DecorationCss, named: NamedFamilies) => {
	if (named.size === 0) return [];
	const text = namedFamilyOf(css["font-family"], named) ?? namedFamilyOf(shorthandFamilyOf(css.font), named) ?? namedFamilyOf(css["--font-text"], named);
	const num = namedFamilyOf(css["--font-num"], named);
	const numVars = num ? familyFitVars(NUM_FIT_PREFIX, num) : "--font-num" in css ? axisVars(NUM_FIT_PREFIX, () => "initial") : [];
	const guards = Object.entries(css).flatMap(([prop, value]) =>
		prop.startsWith("--") && isFamilyProp(prop) && typeof value === "string" ? [[guardOf(prop), guardValueOf(prop, value, named)] as const] : [],
	);
	return [...(text ? familyFitVars(FIT_PREFIX, text) : []), ...numVars, ...guards];
};

export const fitDecorationFonts = (css: DecorationCss | undefined, registeredFamilies: readonly string[] = NO_FAMILIES): DecorationCss | undefined => {
	if (!css) return css;
	const named = namedFamiliesOf(registeredFamilies);
	const context: FitContext = { named, fit: fitOf(css, named) };
	let fitted: DecorationCss | undefined;
	for (const [prop, value] of Object.entries(css)) {
		const fitter = fitterOf(prop);
		if (!fitter || typeof value !== "string") continue;
		const next = withPriority(value, fitter, context);
		if (next === value) continue;
		fitted ??= { ...css };
		fitted[prop] = next;
	}
	if ("--font-text" in css && !("font-weight" in css)) {
		fitted ??= { ...css };
		fitted["font-weight"] = "normal";
	}
	for (const [prop, value] of namedVarsOf(css, named)) {
		if (prop in css) continue;
		fitted ??= { ...css };
		fitted[prop] = value;
	}
	return fitted ?? css;
};
